import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { NodeTypeLogo } from 'src/dashboard/NodeTypeLogo';
import { ImageRenderer } from 'src/common/ImageRenderer';
import {
  DotMenuStrongIcon,
  DragHandleSmallIcon,
  EditStrongIcon,
  SecureStrongIcon,
  SyncStrongIcon,
} from 'src/monet/icons';

import { Node } from 'src/common/interfaces/node.interface';
import { isPlaceholderNode } from 'src/utils/journey';
import { useImageAssetSrcSet } from 'src/utils/use-image-asset-src-set.hook';
import { EventBus } from 'src/common/EventBus';
import { EditorContentActivePanelTypes, EditorContentCardOptions } from 'src/editor/content/card-options';
import { Step } from 'src/common/interfaces/step.interface';
import { Nullable } from 'src/types/nullable.type';
import { useEditorNavigator } from 'src/editor/use-editor-navigator.hook';
import { TooltipComponent } from 'src/common/tooltip/component';
import { LibraryContentCardTypeEnum } from './type.enum';
import { DashboardLibrarySyncedStepsOptions } from 'src/dashboard/library/synced-steps/options';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { apiDeleteJourneyNode, apiRefreshNodeThumbnail } from 'src/utils/journeyApi';
import { functionNoop } from 'src/utils/function/noop';
import { Placement } from '@floating-ui/react-dom';
import { useEditorContext } from 'src/store/editor';
import { useStepEditorContext } from 'src/store/step-editor';

export interface LibraryContentCardInterfaceProps {
  node: Node;
  step?: Step;
  draggable: boolean;
  isDragging?: boolean;
  isActive?: boolean;
  renderLeftIcon?: () => JSX.Element;
  containerClassName?: string;
  name: string;
  type: LibraryContentCardTypeEnum;
  renderAdditional?: () => Nullable<JSX.Element>;
  onClick: (nodeId: number) => void;
  onUpdate?: () => void;
  thumbnailContainerClassName?: string;
}

const cardBaseClassName =
  'flex transition-all items-start space-x-sm border bg-white rounded-lg w-[314px] p-xs cursor-pointer pr-sm';

export const LibraryContentCard = ({
  node,
  draggable,
  isDragging = false,
  renderLeftIcon,
  type,
  renderAdditional = () => null,
  containerClassName = 'items-center',
  isActive,
  name,
  step,
  onClick,
  onUpdate = functionNoop,
  thumbnailContainerClassName = '',
}: LibraryContentCardInterfaceProps) => {
  const activePanelRef = useRef<Nullable<EditorContentActivePanelTypes>>(null);
  const menuPlacementRef = useRef<Placement>();
  const [keepMenuOpen, setKeepMenuOpen] = useState(false);
  const [thumbnailInProgress, setThumbnailInProgress] = useState(false);
  const isPlaceholder = useMemo(() => isPlaceholderNode(node), [node]);
  const { journey } = useEditorContext();
  const { setStateFromNode } = useStepEditorContext();
  const { navigateToContentEdit, navigateToSyncedStepEdit } = useEditorNavigator();
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const isCardTypeLibrary = useMemo(() => type === LibraryContentCardTypeEnum.LIBRARY, [type]);
  const isCardTypeEditor = useMemo(() => type === LibraryContentCardTypeEnum.EDITOR, [type]);
  const isCardTypeSyncedStep = useMemo(() => type === LibraryContentCardTypeEnum.SYNCED_STEP, [type]);

  useEffect(() => {
    const onResetThumbnailStart = (resettedNode: Node) => {
      if (resettedNode.id === node.id) {
        setThumbnailInProgress(true);
      }
    };

    const onResetThumbnailEnd = (resettedNode: Node) => {
      if (resettedNode.id === node.id) {
        setThumbnailInProgress(false);
      }
    };

    EventBus.on('step:reset-thumbnail-start', onResetThumbnailStart);
    EventBus.on('step:reset-thumbnail-end', onResetThumbnailEnd);

    return () => {
      EventBus.off('step:reset-thumbnail-start', onResetThumbnailStart);
      EventBus.off('step:reset-thumbnail-end', onResetThumbnailEnd);
    };
  }, [node.id]);

  const onCardClicked = useCallback(() => {
    onClick(node.id);
  }, [onClick, node.id]);

  const onMenuClosed = useCallback(() => {
    if (!activePanelRef.current) {
      setKeepMenuOpen(false);
    }
  }, []);

  const onMenuOpened = (placement?: Placement) => {
    menuPlacementRef.current = placement;
    setKeepMenuOpen(true);
  };

  const onEditClicked = () => {
    setStateFromNode(node);
    navigateToContentEdit(step!.id, node.id);
  };

  const thumbnailUrl = useMemo(() => {
    if (node.content_type === 'external_link') {
      return node.external_link?.meta_image_url || node.thumbnail?.url;
    } else {
      if (thumbnailInProgress) return '';

      return node?.thumbnail?.url;
    }
  }, [thumbnailInProgress, node?.thumbnail?.url, node.external_link?.meta_image_url, node.content_type]);

  const onSyncStepEditClicked = useCallback(() => {
    navigateToSyncedStepEdit(node.id);
  }, [navigateToSyncedStepEdit, node.id]);

  const onRefreshThumbnail = useCallback(() => {
    setSuccessNotification('New thumbnail being created.');
    EventBus.emit('step:reset-thumbnail-start', node);
    apiRefreshNodeThumbnail(node.id)
      .then((nodeResponse) => {
        onUpdate();
        setSuccessNotification('Successfully regenerated thumbnail.');
        EventBus.emit('step:reset-thumbnail-end', node);
      })
      .catch(() => {
        setErrorNotification('Thumbnail could not be refreshed.');
        EventBus.emit('step:reset-thumbnail-end', node);
      });
  }, [node, onUpdate, setErrorNotification, setSuccessNotification]);

  const onSyncStepDeleted = useCallback(async () => {
    // We don't have the backend api yet
    try {
      await apiDeleteJourneyNode(node.id);
      setSuccessNotification('Synced step successfully deleted.');
      onUpdate();
    } catch {
      setErrorNotification('Synced step deletion failed.');
    }
    onMenuClosed();
  }, [node, onMenuClosed, onUpdate, setSuccessNotification, setErrorNotification]);

  const renderDragIcon = useCallback(() => {
    if (!draggable) return null;

    return (
      <div
        className='w-5 h-5 flex cursor-grab items-center text-bedrock-black'
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DragHandleSmallIcon />
      </div>
    );
  }, [draggable]);

  const isThumbnailDisabled = journey?.branding?.thumbnail_style !== 'image';

  const thumbnailAltContent = useMemo(() => {
    return (
      <div
        className={classNames(
          'flex flex-shrink-0 items-center justify-center w-full h-10 min-w-0 relative rounded-md',
          {
            'bg-bedrock-brand-primary': !thumbnailInProgress && !isPlaceholder,
            'bg-bedrock-gray-medium': thumbnailInProgress || isPlaceholder,
          }
        )}
      >
        {!thumbnailInProgress && (
          <NodeTypeLogo
            showGenericIcon={isThumbnailDisabled || !thumbnailUrl}
            className='max-w-[20px] text-bedrock-white'
            node={node}
          />
        )}
      </div>
    );
  }, [isPlaceholder, thumbnailInProgress, isThumbnailDisabled, thumbnailUrl, node]);

  const leftIcon = !renderLeftIcon ? renderDragIcon() : renderLeftIcon();

  const thumbnailSrcSet = useImageAssetSrcSet(node?.thumbnail || undefined, 'thumbnail_300x300');

  const isDraggingActive = draggable && isDragging;

  return (
    <div
      className={classNames(
        'relative flex',
        {
          'w-[370px]': isCardTypeEditor,
          'space-x-0.5': draggable,
          group: isCardTypeEditor || isCardTypeLibrary,
        },
        containerClassName
      )}
    >
      {leftIcon}
      <div
        className={classNames(cardBaseClassName, {
          'border-bedrock-gray-medium': !isDraggingActive,
          'bedrock-active-card': isActive,
          'bedrock-dragging-step-ring border-transparent': isDraggingActive,
        })}
        onClick={onCardClicked}
      >
        <div
          className={`flex flex-shrink-0 h-10 w-10 min-w-0 relative rounded-md transition-opacity group group-hover:opacity-80 cursor-pointer ${thumbnailContainerClassName}`}
        >
          {isThumbnailDisabled ? (
            <>{thumbnailAltContent}</>
          ) : (
            <>
              {thumbnailUrl ? (
                <>
                  <div className='absolute inset-0 bg-bedrock-black-overlay rounded-md opacity-100 transition ease-in' />
                  <ImageRenderer
                    src={thumbnailUrl}
                    srcSet={thumbnailSrcSet}
                    className='object-cover w-full h-full rounded-md'
                    alt='Step Thumbnail'
                    errorHTML={thumbnailAltContent}
                  />
                </>
              ) : (
                thumbnailAltContent
              )}
            </>
          )}
        </div>
        <div
          className={classNames(
            'w-full text-bedrock-p-small-strong text-bedrock-black antialised transform-gpu transition-opacity !my-auto group-hover:opacity-80',
            node.is_synced && node.is_email_required ? 'max-w-[172px]' : 'max-w-[180px]'
          )}
          style={{ wordBreak: 'break-word' }}
        >
          {name}
        </div>
        <div className='relative mt-2.5 !ml-auto'>
          <div
            className={classNames(
              'absolute right-0 !ml-auto flex opacity-0 group-hover:opacity-100 transition-opacity space-x-sm items-center',
              keepMenuOpen && 'opacity-100'
            )}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {(isCardTypeEditor || isCardTypeLibrary) && (
              <>
                {node.is_synced && (
                  <div className='w-5 h-5 flex items-center justify-center'>
                    <TooltipComponent tooltipContent='Synced Step' placement='top'>
                      <SyncStrongIcon className='text-bedrock-gray-medium' />
                    </TooltipComponent>
                  </div>
                )}
                {node?.is_email_required && (
                  <div className='w-5 h-5 flex items-center justify-center'>
                    <TooltipComponent tooltipContent='Secured Step' placement='top'>
                      <SecureStrongIcon className='text-bedrock-gray-medium' />
                    </TooltipComponent>
                  </div>
                )}
              </>
            )}
            {!isCardTypeSyncedStep && (
              <>
                {!isCardTypeLibrary ? (
                  <>
                    <EditStrongIcon className='has-hover' onClick={onEditClicked} />
                    <EditorContentCardOptions
                      node={node}
                      step={step!}
                      nestedMenuPlacement={menuPlacementRef.current}
                      activePanelRef={activePanelRef}
                      placement='right-start'
                      renderMenuOrigin={() => <DotMenuStrongIcon className='has-hover' />}
                      onMenuOpen={onMenuOpened}
                      onMenuClose={onMenuClosed}
                    />
                  </>
                ) : (
                  <DashboardLibrarySyncedStepsOptions
                    placement='bottom-start'
                    onMenuOpen={onMenuOpened}
                    onEdit={onSyncStepEditClicked}
                    onDelete={onSyncStepDeleted}
                    onRefreshThumbnail={onRefreshThumbnail}
                    renderMenuOrigin={() => <DotMenuStrongIcon className='has-hover' />}
                    onMenuClose={onMenuClosed}
                  />
                )}
              </>
            )}
          </div>
          {!keepMenuOpen && (
            <div className='flex items-center space-x-sm flex-grow-0 transition-opacity opacity-100 group-hover:opacity-0 group-hover:pointer-events-none justify-end'>
              {!isCardTypeEditor && (
                <>
                  {node.is_synced && (
                    <div className='w-5 h-5 flex items-center justify-center'>
                      <TooltipComponent tooltipContent='Synced Step' placement='top'>
                        <SyncStrongIcon className='text-bedrock-gray-medium' />
                      </TooltipComponent>
                    </div>
                  )}
                  {node?.is_email_required && (
                    <div className='w-5 h-5 flex items-center justify-center'>
                      <TooltipComponent tooltipContent='Secured Step' placement='top'>
                        <SecureStrongIcon className='text-bedrock-gray-medium' />
                      </TooltipComponent>
                    </div>
                  )}
                </>
              )}
              <NodeTypeLogo className='max-w-[20px]' node={node} />
            </div>
          )}
        </div>
      </div>
      {/* don't render additional components while dragging. this is to prevent tooltip-like fixed components from
      being rendered in the wrong position */}
      {isCardTypeEditor && !isDragging && renderAdditional()}
    </div>
  );
};
